/**
 * TenantLlmPolicyPage — Tenant LLM selection policy (ADR-042 §3.3).
 *
 * Admin-only (tenant scope). Path: /settings/llm-policy
 *
 * Sections:
 *   1. Stored tenant policy (all NULL-able → inherit from DR)
 *   2. Effective policy with source_trace (explain)
 *   3. Per-catalog overrides (CRUD)
 *   4. Simulate
 */

import { useEffect, useRef, useState } from 'react';
import { useI18n } from '@/i18n';
import {
  Loader2,
  Save,
  Sparkles,
  Info,
  Plus,
  Trash2,
  Play,
  Shield,
  SlidersHorizontal,
} from 'lucide-react';
import { PageHeader } from '@vanguard-atlas/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { getErrorMessage } from '@/lib/api-client';
import {
  useTenantLlmPolicy,
  useUpdateTenantLlmPolicy,
  useTenantEffectivePolicy,
  useUpsertCatalogOverride,
  useDeleteCatalogOverride,
  useSimulateTenantLlmPolicy,
} from '@/features/llm-config/hooks/useLlmPolicy';
import { useAvailableCatalogs } from '@/features/llm-config/hooks/useAvailableCatalogs';
import type {
  TenantLlmPolicyUpdate,
  CatalogOverrideUpsert,
  PolicyStrategy,
  ByopPolicy,
  HealthThreshold,
} from '@/features/llm-config/types/policy';
import {
  POLICY_STRATEGIES,
  BYOP_POLICIES,
} from '@/features/llm-config/types/policy';
import { PipelinePolicySections } from '@/features/llm-config/components/PipelinePolicySections';
import {
  BYOP_LABELS,
  HEALTH_LABELS,
  STRATEGY_LABELS,
} from '@/features/llm-config/lib/policyLabels';
import { PROVIDER_METADATA } from '@/features/llm-config/types';

const DEFAULT_DR_CODE = 'eu-w1';
const ALL_PROVIDER_TYPES = Object.keys(PROVIDER_METADATA);

/**
 * Render a tri-state selector for NULL-able policy fields:
 *   - null → "Inherit from DR default"
 *   - true/value → explicit override
 */
function InheritOrValue<T extends string | boolean | number>({
  label,
  value,
  onChange,
  children,
}: {
  label: string;
  value: T | null | undefined;
  onChange: (v: T | null) => void;
  children: (setValue: (v: T) => void) => React.ReactNode;
}) {
  const { t } = useI18n();
  const inherits = value === null || value === undefined;
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium flex-1">{label}</span>
        <label className="flex items-center gap-1 text-xs text-gray-500">
          <input
            type="checkbox"
            aria-label={t('policy.inheritField', { field: label })}
            checked={inherits}
            onChange={(e) => onChange(e.target.checked ? null : ('' as T))}
          />
          {t('policy.inherit')}
        </label>
      </div>
      {!inherits && <div className="pl-2">{children((v) => onChange(v))}</div>}
    </div>
  );
}

export function TenantLlmPolicyPage() {
  const { t } = useI18n();
  const [drCode, setDrCode] = useState(DEFAULT_DR_CODE);

  const { data: policy, isLoading, error } = useTenantLlmPolicy();
  const update = useUpdateTenantLlmPolicy();

  const [form, setForm] = useState<TenantLlmPolicyUpdate>({});
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const hydrated = useRef(false);

  // Hydrate form when policy loads
  useEffect(() => {
    if (policy && !hydrated.current) {
      hydrated.current = true;
      setForm({
        strategy: policy.strategy,
        provider_preference: policy.provider_preference,
        provider_weights: policy.provider_weights,
        allowed_providers: policy.allowed_providers,
        allowed_models: policy.allowed_models,
        byop_policy: policy.byop_policy,
        data_residency: policy.data_residency,
        cost_ceiling_usd_per_1k: policy.cost_ceiling_usd_per_1k,
        fallback_enabled: policy.fallback_enabled,
        fallback_max_attempts: policy.fallback_max_attempts,
        health_threshold: policy.health_threshold,
        sticky_session: policy.sticky_session,
        allow_emergency_platform_fallback:
          policy.allow_emergency_platform_fallback,
        policy_enabled: policy.policy_enabled,
        // Sprint 278 (ROUTE-PIPE-1/A — REQ-LLM-006 §8.1); null = heredar.
        routing_enabled: policy.routing_enabled,
        routing_mode: policy.routing_mode,
        routing_on_failure: policy.routing_on_failure,
        routing_confidence_threshold: policy.routing_confidence_threshold,
        routing_timeout_ms: policy.routing_timeout_ms,
        planning_on_failure: policy.planning_on_failure,
        grounding_check: policy.grounding_check,
        routing_budget_tokens: policy.routing_budget_tokens,
        optimization_enabled: policy.optimization_enabled,
        max_loop_iterations: policy.max_loop_iterations,
        frontier_agents_enabled: policy.frontier_agents_enabled,
        frontier_max_duration_minutes: policy.frontier_max_duration_minutes,
        frontier_max_concurrent_runs: policy.frontier_max_concurrent_runs,
        frontier_on_unavailable: policy.frontier_on_unavailable,
        plan_review: policy.plan_review,
        retrieval_gating_enabled: policy.retrieval_gating_enabled,
        foreground_timeout_seconds: policy.foreground_timeout_seconds,
        delegation_sync_timeout_seconds:
          policy.delegation_sync_timeout_seconds,
        delegation_background_timeout_seconds:
          policy.delegation_background_timeout_seconds,
        fanout_aggregation_timeout_seconds:
          policy.fanout_aggregation_timeout_seconds,
      });
    }
  }, [policy]);

  const handleSave = async () => {
    // ISS-701: una allowlist explícita vacía no se envía; null = todos.
    if (
      form.allowed_models !== null &&
      form.allowed_models !== undefined &&
      form.allowed_models.length === 0
    ) {
      setSuccessMsg(null);
      setErrorMsg(t('policy.modelsSelectionRequired'));
      return;
    }
    setErrorMsg(null);
    setSuccessMsg(null);
    try {
      await update.mutateAsync({ drCode, update: form });
      setSuccessMsg(t('policy.tenantSaved'));
    } catch (err) {
      setErrorMsg(getErrorMessage(err));
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !policy) {
    return (
      <div className="rounded-md border border-red-300 bg-red-50 dark:bg-red-900/20 p-4 text-sm text-red-700 dark:text-red-300">
        {t('policy.tenantLoadError', {
          message: error instanceof Error ? error.message : t('policy.unknownError'),
        })}
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl">
      <PageHeader
        icon={Sparkles}
        title={t('tenant.llmPolicy.title')}
        subtitle={t('policy.tenantSubtitle')}
        actions={
          <>
            <span className="text-xs text-gray-500">{t('policy.drLabel')}</span>
            <Input
              value={drCode}
              onChange={(e) => setDrCode(e.target.value || DEFAULT_DR_CODE)}
              className="w-28 font-mono text-sm"
            />
          </>
        }
        testId="llm-policy-header"
      />

      {errorMsg && (
        <div className="rounded-md border border-red-300 bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-300">
          {errorMsg}
        </div>
      )}
      {successMsg && (
        <div className="rounded-md border border-green-300 bg-green-50 dark:bg-green-900/20 p-3 text-sm text-green-700 dark:text-green-300">
          {successMsg}
        </div>
      )}

      <TenantPolicyForm form={form} setForm={setForm} />

      {/* Pipeline policy — Sprint 278 (ROUTE-PIPE-1/A — REQ-LLM-006 §8.1).
          Cada campo con InheritOrValue: null = heredar de la capa DR. */}
      <PipelinePolicySections
        mode="inherit"
        value={form}
        onChange={(patch) => setForm({ ...form, ...patch })}
      />

      <div className="flex justify-end gap-3">
        <Button onClick={handleSave} disabled={update.isPending}>
          {update.isPending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          {t('policy.tenantSave')}
        </Button>
      </div>

      {/* ---- Effective policy (explain) ---------------------------------- */}
      <EffectivePolicyViewer drCode={drCode} />

      {/* ---- Per-catalog overrides --------------------------------------- */}
      <CatalogOverridesSection
        drCode={drCode}
        overrides={policy.per_catalog_overrides ?? {}}
      />

      {/* ---- Simulate ---------------------------------------------------- */}
      <SimulateSection drCode={drCode} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function TenantPolicyForm({
  form,
  setForm,
}: {
  form: TenantLlmPolicyUpdate;
  setForm: (f: TenantLlmPolicyUpdate) => void;
}) {
  const { t } = useI18n();
  return (
    <section className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 space-y-5">
      <h2 className="flex items-center gap-2 text-lg font-semibold"><Shield className="h-5 w-5 text-primary" aria-hidden="true" />{t('policy.storedTitle')}</h2>

      <InheritOrValue
        label={t('policy.strategyLabel')}
        value={form.strategy}
        onChange={(v) => setForm({ ...form, strategy: v as PolicyStrategy | null })}
      >
        {(set) => (
          <select
            value={(form.strategy as string) || ''}
            onChange={(e) => set(e.target.value as PolicyStrategy)}
            className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
          >
            <option value="">{t('policy.selectPlaceholder')}</option>
            {POLICY_STRATEGIES.map((s) => (
              <option key={s} value={s}>
                {t(STRATEGY_LABELS[s])}
              </option>
            ))}
          </select>
        )}
      </InheritOrValue>

      <InheritOrValue
        label={t('policy.allowedProviders')}
        value={form.allowed_providers as unknown as string}
        onChange={(v) =>
          setForm({
            ...form,
            allowed_providers:
              v === null ? null : (v as unknown as string[]) ?? [],
          })
        }
      >
        {() => (
          <div className="flex flex-wrap gap-2">
            {ALL_PROVIDER_TYPES.map((pt) => {
              const list = form.allowed_providers ?? [];
              const included = list.includes(pt);
              return (
                <button
                  key={pt}
                  type="button"
                  onClick={() => {
                    const next = included
                      ? list.filter((x) => x !== pt)
                      : [...list, pt];
                    setForm({ ...form, allowed_providers: next });
                  }}
                  className={`rounded-md border px-3 py-1 text-sm ${
                    included
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-gray-300 dark:border-gray-600 text-gray-500'
                  }`}
                >
                  {PROVIDER_METADATA[
                    pt as keyof typeof PROVIDER_METADATA
                  ]?.displayName ?? pt}
                </button>
              );
            })}
          </div>
        )}
      </InheritOrValue>

      <AllowedModelsSelector form={form} setForm={setForm} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium">{t('policy.byopPolicy')}</label>
          <select
            value={form.byop_policy ?? 'prefer_platform'}
            onChange={(e) =>
              setForm({ ...form, byop_policy: e.target.value as ByopPolicy })
            }
            className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
          >
            {BYOP_POLICIES.map((p) => (
              <option key={p} value={p}>
                {t(BYOP_LABELS[p])}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-sm font-medium">{t('policy.costCeiling')}</label>
          <Input
            className="mt-1"
            type="number"
            step="0.0001"
            placeholder={t('policy.costCeilingPlaceholder')}
            value={form.cost_ceiling_usd_per_1k ?? ''}
            onChange={(e) =>
              setForm({
                ...form,
                cost_ceiling_usd_per_1k: e.target.value || null,
              })
            }
          />
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={!!form.sticky_session}
            onChange={(e) =>
              setForm({ ...form, sticky_session: e.target.checked })
            }
          />
          {t('policy.stickySession')}
        </label>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={!!form.allow_emergency_platform_fallback}
            onChange={(e) =>
              setForm({
                ...form,
                allow_emergency_platform_fallback: e.target.checked,
              })
            }
          />
          {t('policy.emergencyFallback')}
        </label>

        <InheritOrValue
          label={t('admin.drPolicy.enablePolicy')}
          value={form.policy_enabled}
          onChange={(v) =>
            setForm({ ...form, policy_enabled: v as boolean | null })
          }
        >
          {(set) => (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!form.policy_enabled}
                onChange={(e) => set(e.target.checked)}
              />
              {t('admin.drPolicy.enabled')}
            </label>
          )}
        </InheritOrValue>

        <InheritOrValue
          label={t('policy.fallbackEnabled')}
          value={form.fallback_enabled}
          onChange={(v) =>
            setForm({ ...form, fallback_enabled: v as boolean | null })
          }
        >
          {(set) => (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!form.fallback_enabled}
                onChange={(e) => set(e.target.checked)}
              />
              {t('admin.drPolicy.enabled')}
            </label>
          )}
        </InheritOrValue>

        <InheritOrValue
          label={t('policy.maxAgentSteps')}
          value={form.max_agent_steps}
          onChange={(v) =>
            setForm({ ...form, max_agent_steps: v as number | null })
          }
        >
          {(set) => (
            <Input
              type="number"
              min="1"
              className="w-32"
              value={form.max_agent_steps ?? 50}
              onChange={(e) =>
                set(Math.max(1, Number(e.target.value) || 50))
              }
            />
          )}
        </InheritOrValue>

        <InheritOrValue
          label={t('policy.fallbackMaxAttempts')}
          value={form.fallback_max_attempts}
          onChange={(v) =>
            setForm({ ...form, fallback_max_attempts: v as number | null })
          }
        >
          {(set) => (
            <Input
              type="number"
              min="0"
              max="5"
              className="w-24"
              value={form.fallback_max_attempts ?? 0}
              onChange={(e) => set(Number(e.target.value))}
            />
          )}
        </InheritOrValue>

        <InheritOrValue
          label={t('policy.healthThreshold')}
          value={form.health_threshold}
          onChange={(v) =>
            setForm({ ...form, health_threshold: v as HealthThreshold | null })
          }
        >
          {(set) => (
            <select
              value={form.health_threshold ?? 'skip_degraded'}
              onChange={(e) => set(e.target.value as HealthThreshold)}
              className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
            >
              <option value="skip_degraded">
                {t('policy.healthSkipDegraded')}
              </option>
              <option value="allow_degraded">
                {t('policy.healthAllowDegraded')}
              </option>
            </select>
          )}
        </InheritOrValue>

        <InheritOrValue
          label={t('policy.dataResidency')}
          value={form.data_residency as unknown as string}
          onChange={(v) =>
            setForm({
              ...form,
              data_residency:
                v === null
                  ? null
                  : (v as unknown as string)
                      .split(',')
                      .map((s) => s.trim())
                      .filter(Boolean),
            })
          }
        >
          {(set) => (
            <Input
              placeholder={t('policy.dataResidencyPlaceholder')}
              value={(form.data_residency ?? []).join(',')}
              onChange={(e) => set(e.target.value as unknown as string)}
            />
          )}
        </InheritOrValue>
      </div>
    </section>
  );
}

function AllowedModelsSelector({
  form,
  setForm,
}: {
  form: TenantLlmPolicyUpdate;
  setForm: (f: TenantLlmPolicyUpdate) => void;
}) {
  const { t } = useI18n();
  const { data: catalogs, isLoading } = useAvailableCatalogs();
  const [search, setSearch] = useState('');
  const allAllowed = form.allowed_models === null || form.allowed_models === undefined;
  const selected = form.allowed_models ?? [];
  const filtered = (catalogs ?? []).filter(
    (catalog) =>
      !search ||
      catalog.slug.toLowerCase().includes(search.toLowerCase()) ||
      catalog.displayName.toLowerCase().includes(search.toLowerCase()),
  );

  const toggle = (slug: string) => {
    const next = selected.includes(slug)
      ? selected.filter((item) => item !== slug)
      : [...selected, slug];
    setForm({ ...form, allowed_models: next });
  };

  return (
    <div className="space-y-3 rounded-md border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-medium">{t('policy.allowedModels')}</div>
          <p className="text-xs text-gray-500">{t('policy.allowedModelsHint')}</p>
        </div>
        <label className="flex items-center gap-2 text-sm whitespace-nowrap">
          <input
            type="checkbox"
            checked={allAllowed}
            onChange={(e) =>
              setForm({
                ...form,
                allowed_models: e.target.checked ? null : [],
              })
            }
          />
          {t('policy.allModels')}
        </label>
      </div>

      {!allAllowed && (
        <div className="space-y-2">
          <Input
            placeholder={t('policy.searchModels')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {isLoading ? (
            <p className="text-xs text-gray-500">{t('policy.modelsLoading')}</p>
          ) : filtered.length === 0 ? (
            <p className="text-xs text-gray-500">{t('policy.modelsEmpty')}</p>
          ) : (
            <div className="max-h-52 overflow-y-auto rounded-md border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-800">
              {filtered.map((catalog) => (
                <label
                  key={catalog.slug}
                  className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-800/50"
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(catalog.slug)}
                    onChange={() => toggle(catalog.slug)}
                  />
                  <span className="flex-1">{catalog.displayName}</span>
                  <span className="font-mono text-xs text-gray-400">
                    {catalog.slug}
                  </span>
                </label>
              ))}
            </div>
          )}
          <p className="text-xs text-gray-500">
            {t('policy.modelsSelected', { count: selected.length })}
          </p>
          {selected.length === 0 && (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              {t('policy.modelsSelectionRequired')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function EffectivePolicyViewer({ drCode }: { drCode: string }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const { data, isFetching } = useTenantEffectivePolicy({
    drCode,
    enabled: open,
  });

  return (
    <section className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Info className="h-4 w-4 text-primary" />
          {t('policy.effectiveTitle')}
        </h2>
        <Button variant="outline" size="sm" onClick={() => setOpen(!open)}>
          {open ? t('policy.effectiveHide') : t('policy.effectiveShow')}
        </Button>
      </div>
      {open && (
        <>
          {isFetching && (
            <div className="text-sm text-gray-500">{t('policy.loading')}</div>
          )}
          {data && (
            <div className="space-y-2 text-sm">
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline">
                  {t('policy.effectiveStrategy', {
                    value: t(STRATEGY_LABELS[data.strategy]),
                  })}
                </Badge>
                <Badge variant="outline">
                  {t('policy.effectiveByop', {
                    value: t(BYOP_LABELS[data.byop_policy]),
                  })}
                </Badge>
                <Badge variant="outline">
                  {t('policy.effectiveHealth', {
                    value: t(HEALTH_LABELS[data.health_threshold]),
                  })}
                </Badge>
                <Badge variant="outline">
                  {t('policy.effectiveFallback', {
                    state: data.fallback_enabled
                      ? t('policy.stateOn')
                      : t('policy.stateOff'),
                    attempts: data.fallback_max_attempts,
                  })}
                </Badge>
                <Badge variant="outline">
                  {t('policy.effectiveSticky', {
                    state: data.sticky_session
                      ? t('policy.stateYes')
                      : t('policy.stateNo'),
                  })}
                </Badge>
              </div>
              <div>
                <span className="font-medium">
                  {t('policy.effectivePreference')}
                </span>{' '}
                {(data.provider_preference ?? []).join(' → ') || '—'}
              </div>
              <div>
                <span className="font-medium">
                  {t('policy.effectiveAllowed')}
                </span>{' '}
                {(data.allowed_providers ?? []).join(', ') || '—'}
              </div>
              <details className="text-xs">
                <summary className="cursor-pointer text-gray-500">
                  {t('policy.effectiveSourceTrace', {
                    count: Object.keys(data.source_trace ?? {}).length,
                  })}
                </summary>
                <pre className="mt-2 rounded bg-gray-50 dark:bg-gray-800 p-3 overflow-x-auto">
                  {JSON.stringify(data.source_trace, null, 2)}
                </pre>
              </details>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function CatalogOverridesSection({
  drCode,
  overrides,
}: {
  drCode: string;
  overrides: Record<string, Record<string, unknown>>;
}) {
  const { t } = useI18n();
  const [adding, setAdding] = useState(false);
  const [newSlug, setNewSlug] = useState('');
  const [newStrategy, setNewStrategy] = useState<PolicyStrategy | ''>('');
  const upsert = useUpsertCatalogOverride();
  const remove = useDeleteCatalogOverride();
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const entries = Object.entries(overrides);

  const handleAdd = async () => {
    if (!newSlug) return;
    setErrorMsg(null);
    const payload: CatalogOverrideUpsert = {
      catalog_slug: newSlug,
      strategy: newStrategy || null,
    };
    try {
      await upsert.mutateAsync({ drCode, override: payload });
      setNewSlug('');
      setNewStrategy('');
      setAdding(false);
    } catch (err) {
      setErrorMsg(getErrorMessage(err));
    }
  };

  const handleDelete = async (slug: string) => {
    setErrorMsg(null);
    try {
      await remove.mutateAsync(slug);
    } catch (err) {
      setErrorMsg(getErrorMessage(err));
    }
  };

  return (
    <section className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-lg font-semibold"><SlidersHorizontal className="h-5 w-5 text-primary" aria-hidden="true" />{t('policy.overridesTitle')}</h2>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setAdding(!adding)}
        >
          <Plus className="h-4 w-4 mr-1" /> {t('policy.overridesAdd')}
        </Button>
      </div>

      {errorMsg && (
        <div className="rounded-md border border-red-300 bg-red-50 dark:bg-red-900/20 p-2 text-xs text-red-700 dark:text-red-300">
          {errorMsg}
        </div>
      )}

      {adding && (
        <div className="flex flex-wrap items-end gap-2 rounded-md border border-dashed border-gray-300 dark:border-gray-600 p-3">
          <div>
            <label className="block text-xs text-gray-500">
              {t('policy.catalogSlug')}
            </label>
            <Input
              value={newSlug}
              onChange={(e) => setNewSlug(e.target.value)}
              placeholder={t('policy.slugPlaceholder')}
              className="w-60 font-mono text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500">
              {t('policy.strategyLabel')}
            </label>
            <select
              value={newStrategy}
              onChange={(e) =>
                setNewStrategy(e.target.value as PolicyStrategy | '')
              }
              className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
            >
              <option value="">{t('policy.overridesInheritOption')}</option>
              {POLICY_STRATEGIES.map((s) => (
                <option key={s} value={s}>
                  {t(STRATEGY_LABELS[s])}
                </option>
              ))}
            </select>
          </div>
          <Button onClick={handleAdd} disabled={upsert.isPending || !newSlug}>
            {upsert.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              t('policy.save')
            )}
          </Button>
          <Button variant="ghost" onClick={() => setAdding(false)}>
            {t('policy.cancel')}
          </Button>
        </div>
      )}

      {entries.length === 0 ? (
        <div className="text-sm text-gray-500">{t('policy.overridesEmpty')}</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-gray-500 uppercase">
            <tr>
              <th className="px-2 py-1">{t('policy.catalogSlug')}</th>
              <th className="px-2 py-1">{t('policy.overridesFields')}</th>
              <th className="px-2 py-1 w-10" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {entries.map(([slug, ov]) => (
              <tr key={slug}>
                <td className="px-2 py-2 font-mono text-xs">{slug}</td>
                <td className="px-2 py-2 text-xs text-gray-600">
                  {Object.keys(ov).join(', ') || '—'}
                </td>
                <td className="px-2 py-2 text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(slug)}
                    disabled={remove.isPending}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function SimulateSection({ drCode }: { drCode: string }) {
  const { t } = useI18n();
  const [slugs, setSlugs] = useState('');
  const simulate = useSimulateTenantLlmPolicy();
  const [result, setResult] = useState<
    Awaited<ReturnType<typeof simulate.mutateAsync>> | null
  >(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const handleRun = async () => {
    setErrorMsg(null);
    setResult(null);
    const parsed = slugs
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (!parsed.length) return;
    try {
      const res = await simulate.mutateAsync({
        catalog_slugs: parsed,
        dr_code: drCode,
      });
      setResult(res);
    } catch (err) {
      setErrorMsg(getErrorMessage(err));
    }
  };

  return (
    <section className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 space-y-3">
      <h2 className="flex items-center gap-2 text-lg font-semibold"><Play className="h-5 w-5 text-primary" aria-hidden="true" />{t('policy.simulateTitle')}</h2>
      <p className="text-sm text-gray-500">
        {t('policy.simulateHint')}
      </p>
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <Input
            value={slugs}
            onChange={(e) => setSlugs(e.target.value)}
            placeholder={t('policy.simulatePlaceholder')}
          />
        </div>
        <Button onClick={handleRun} disabled={simulate.isPending}>
          {simulate.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            t('policy.simulateRun')
          )}
        </Button>
      </div>
      {errorMsg && (
        <div className="rounded-md border border-red-300 bg-red-50 dark:bg-red-900/20 p-2 text-xs text-red-700 dark:text-red-300">
          {errorMsg}
        </div>
      )}
      {result && (
        <div className="space-y-3">
          {result.any_empty && (
            <div className="rounded-md border border-yellow-300 bg-yellow-50 dark:bg-yellow-900/20 p-2 text-xs text-yellow-800 dark:text-yellow-300">
              {t('policy.simulateAnyEmpty')}
            </div>
          )}
          {result.results.map((r) => (
            <div
              key={r.catalog_slug}
              className="rounded-md border border-gray-200 dark:border-gray-700 p-3"
            >
              <div className="font-mono text-xs mb-1">
                {r.catalog_slug}{' '}
                {r.empty && (
                  <Badge className="bg-red-100 text-red-800 ml-2 text-xs">
                    {t('policy.simulateEmptyBadge')}
                  </Badge>
                )}
              </div>
              <pre className="text-xs bg-gray-50 dark:bg-gray-800 rounded p-2 overflow-x-auto">
                {JSON.stringify(r.attempts, null, 2)}
              </pre>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export default TenantLlmPolicyPage;
